import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import Play from '@/images/Play.svg';
import { PreviewType } from '@/pages/campaign/CreatePage/types';
import { logger } from '@/services/logger';
import { Auth, ConnectionState } from '@d-id/client-sdk';
import styled from '@emotion/styled';
import { useCallback, useRef, useState } from 'react';
import { UpdateCampaignSessionPayload } from '../../types';
import { CircularWithValueLabel } from './AgentLoader';
import { IdleComponent } from './IdleComponent';
import { useAgentApi } from './useAgentApi';
import { useAgentState } from './useAgentState';

const Container = styled.div<{ preview?: PreviewType; embed?: boolean }>`
    position: relative;
    outline: none;
    height: ${({ embed }) => (embed ? '100%' : '512px')};
    width: ${({ embed }) => (embed ? '100%' : '512px')};
    aspect-ratio: 1;

    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%), white;

    ${({ preview, embed }) =>
        !embed &&
        `
    @media (max-width: '768px'), ${preview && 'screen'} {
        width: 100%;
        height: auto;
        background: white;
    }
      `};
`;

const StyledVideo = styled.video<{ preview?: PreviewType; show?: boolean }>`
    border-radius: 6px;
    position: absolute;
    top: 0;
    left: 0;
    height: ${({ preview }) => (preview === PreviewType.DESKTOP ? '512px' : '100%')};
    width: ${({ preview }) => (preview === PreviewType.DESKTOP ? '512px' : '100%')};
    object-fit: cover;
    z-index: ${({ show }) => (show ? 10 : 0)};
`;

const CenterContainer = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'flex' : 'none')};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 20;
`;

const PlayButtonContainer = styled(CenterContainer)<{ show: boolean }>`
    border-radius: 170px;
    background: rgba(0, 0, 0, 0.35);
    width: 95px;
    height: 95px;
    padding: 27.154px;
    align-items: flex-start;
    gap: 27.154px;
    cursor: pointer;
`;

const PlayButton = styled(MaskedIcon)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

interface AgentProps {
    agentId: string;
    auth: Auth;
    script: string;
    sendAnalytic: (data: UpdateCampaignSessionPayload) => void;
    preview?: PreviewType;
    duration: number;
    embed?: boolean;
}

export const Agent = ({ agentId, auth, script, sendAnalytic, preview, duration, embed }: AgentProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [initTime, setInitTime] = useState(0);
    const { agentAPI, connectionState, videoState } = useAgentApi({ agentId, videoRef, auth });
    const [sentAnalyticsEvents, setSentAnalyticsEvents] = useState<Map<keyof UpdateCampaignSessionPayload, boolean>>(new Map());

    const sendVideoAnalytic = useCallback(
        (data: keyof UpdateCampaignSessionPayload) => {
            if (sentAnalyticsEvents.has(data)) {
                return;
            }

            sendAnalytic({ [data]: true });
            setSentAnalyticsEvents(prev => new Map(prev.set(data, true)));
        },

        [sendAnalytic, sentAnalyticsEvents]
    );

    const speak = useCallback(async () => {
        if (!agentAPI || !videoRef.current) {
            return;
        }

        try {
            videoRef.current.muted = false;
            await agentAPI.speak({
                type: 'text',
                provider: agentAPI.agent.presenter.voice,
                input: script,
                ssml: true,
            });

            sendVideoAnalytic('video_viewed');
        } catch (error) {
            logger.error(`Campaign agent Failed to speak`, { agentId, error });
            toast.error({
                title: 'Please try again later.',
            });
        }
    }, [agentAPI, videoRef, script, sendVideoAnalytic]);

    const { showVideo, showPlayButton, speakLoading, setShouldStartTalk, setSpeakLoading } = useAgentState({
        connectionState,
        videoState,
        agentPreviewReady: !!agentAPI?.agent.presenter.idle_video || !!agentAPI?.agent.presenter.thumbnail,
        speak,
    });

    const onClick = async () => {
        setSpeakLoading(true);
        setShouldStartTalk(true);
    };

    const onTimeUpdate = () => {
        if (!videoRef.current || connectionState !== ConnectionState.Connected) {
            return;
        }

        const currentTime = videoRef.current.currentTime;

        if (!initTime) {
            setInitTime(currentTime);
            return;
        }

        const watched = currentTime - initTime;
        const percentage = (watched / duration) * 100;
        if (percentage > 90) {
            sendVideoAnalytic('video_90_percent_viewed');
        } else if (percentage > 10) {
            sendVideoAnalytic('video_10_percent_viewed');
        }
    };

    return (
        <Container preview={preview} embed={embed}>
            <StyledVideo ref={videoRef} muted autoPlay playsInline preview={preview} show={showVideo} onTimeUpdate={onTimeUpdate} />
            <IdleComponent
                // TODO: fix agent type in agent sdk
                // @ts-ignore
                agentStatus={agentAPI?.agent.status}
                idleVideo={agentAPI?.agent.presenter.idle_video}
                preview={preview}
                thumbnail={agentAPI?.agent.presenter.thumbnail}
                show={!showVideo}
                speakLoading={speakLoading}
            />
            <PlayButtonContainer show={showPlayButton} onClick={onClick} id="campaign-play-btn">
                <PlayButton src={Play} size="40px" color="white" />
            </PlayButtonContainer>

            {speakLoading && (
                <CenterContainer show>
                    <CircularWithValueLabel size="95px" />
                </CenterContainer>
            )}
        </Container>
    );
};
